import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Box from "@mui/material/Box";
import { NoActivity } from "@config/images";
import DynamicImage from "@ds/components/DynamicImage";
import { cleanObject } from "@helpers/common";
// import getGenerateToken from "@helpers/getGenerateToken";
import { setWarning } from "@redux/actions";
import { setClear } from "@redux/slices/warning";
import { setOpenSnackbar } from "@redux/slices/rootSnackbar";
import { INTEREST_FORM_SERVICES } from "@repositories/interest-form";
import { useTranslation } from "next-i18next";
import type {
  TCity,
  TCompanySizeAction,
  // TCountry,
  TIndustry,
  TInterestedForm,
  TProductFields,
} from "../type";
import { getClientId, gtmCustomEvent } from "@helpers/analytics";
import { useLanguage } from "@utils/useLanguage";
import { PROFILE_API } from "@libraries/repositories/profile/profile.hooks";

function useAction(products: TProductFields, productName?: string) {
  const [, setLoading] = useState(false);
  const [dialCode, setDialCode] = useState("+62");
  const [industryData, setIndustryData] = useState<TIndustry[]>([]);
  // const [countryData, setCountryData] = useState<TCountry[]>([]);
  const [cityData, setCityData] = useState<TCity[]>([]);
  const [isChecked, setIsChecked] = useState(false);
  const [companySizeData, setCompanySizeData] = useState<TCompanySizeAction[]>([]);
  const [isLoad, setIsLoad] = useState(false);
  const [initialCityParam, setInitialCityParam] = useState({
    keyword: "",
  });
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation("common");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [initialValueForm, setInitialValueForm] = useState<Partial<TInterestedForm>>({
    fullName: "",
    companyName: "",
    industry: "",
    companySize: "",
    city: "",
    email: "",
    phoneNumber: "",
    message: "",
    dialCode: "",
    products: [],
  });

  const _getProfileUser = async () => {
    try {
      setLoading(true);
      const { data } = await PROFILE_API.getProfile();
      setInitialValueForm({
        companySize: "",
        message: "",
        dialCode: "",
        companyName: data.businessName,
        fullName: data.name || data.fullName || "",
        email: data.email || "",
        phoneNumber: data.phoneNumber.slice(1) || "",
        industry: data.industry || "",
        city: data.city || "",
      });
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  const _getDataIndustry = async () => {
    try {
      const { data, success } = await INTEREST_FORM_SERVICES.GET_INDUSTRY_DATA(language);
      if (success) {
        const mappedIndustry = data.map((item: any) => ({
          id: item.id,
          label: item.title,
          value: item.title,
        }));
        setIndustryData(mappedIndustry);
      }
    } catch (err: any) {
      dispatch(
        setOpenSnackbar({
          message: err?.message,
          variant: "failed",
        }),
      );
      setIndustryData([]);
    }
  };

  // const _getDataCountry = async () => {
  //   const token: string = await getGenerateToken();
  //   try {
  //     const { data, success } = await INTEREST_FORM_SERVICES.GET_COUNTRY_DATA(token);
  //     if (success) {
  //       setCountryData(data);
  //       setDialCode("+62");
  //     }
  //   } catch (err: any) {
  //     dispatch(
  //       setOpenSnackbar({
  //         message: err?.message,
  //         variant: "failed",
  //       }),
  //     );
  //     setCountryData([]);
  //     setDialCode("");
  //   }
  // };

  const _getDataCity = async () => {
    const params = cleanObject({ ...initialCityParam });
    params.keyword = "";
    try {
      const { data, success } = await INTEREST_FORM_SERVICES.GET_CITY_DATA(params);
      if (success) {
        const mappedCity = data.map((item: any) => ({
          id: item.id,
          label: item.name,
          value: item.name,
        }));
        setCityData(mappedCity);
      }
    } catch (err: any) {
      dispatch(
        setOpenSnackbar({
          message: err?.message,
          variant: "failed",
        }),
      );
      setCityData([]);
    }
  };

  const _getDataCompanySize = async () => {
    try {
      const { data, success } = await INTEREST_FORM_SERVICES.GET_COMPANY_SIZE(language);
      if (success) {
        setCompanySizeData(data);
      }
    } catch (err: any) {
      dispatch(
        setOpenSnackbar({
          message: err?.message,
          variant: "failed",
        }),
      );
    }
  };

  const onCityChange = (event: string) => {
    setCityData([]);
    setInitialCityParam({ ...initialCityParam, keyword: event });
  };

  const onVerifyCaptcha = async () => {
    if (!executeRecaptcha) {
      return;
    }
    const clientToken = await executeRecaptcha();
    return clientToken;
  };

  const onSubmitForm = async (values: Partial<TInterestedForm>, actions: any) => {
    const clientToken = await onVerifyCaptcha();
    setIsLoad(true);
    const payload: Partial<TInterestedForm> = {
      ...values,
      source: "mytens_web",
      products: [products],
      dialCode: dialCode,
      companySize: companySizeData.find((item) => item.value === values.companySize)?.value + "",
      industry: industryData.find((item) => item.label === values.industry)?.label + "",
      phoneNumber: values.phoneNumber + "",
      userPseudo: {clientId: getClientId() || ""},
    };

    try {
      const { success, message } = await INTEREST_FORM_SERVICES.POST_INTEREST_FORM(
        {
          ...payload,
        },
        clientToken,
        language
      );

      if (success) {
        dispatch(
          setWarning({
            maxWidth: "xs",
            image: (
              <Box margin="auto" width={300}>
                <DynamicImage
                  src={NoActivity}
                  alt="success-submit-interest"
                />
              </Box>
            ),
            messages: [
              [<Box margin="10px">{t("interest.titleModalMessage")}</Box>, "", "800", "title"],
              [<Box margin="10px">{t("interest.descriptionModalMessage")}</Box>],
            ],
            applyButton: [
              {
                label: t("interest.final"),
                variant: "primary",
                onClick: () => {
                  dispatch(setClear());
                  router.replace(router.asPath.substring(0, router.asPath.lastIndexOf('/')));
                },
              },
            ],
          }),
        );
        // Send GTM Event Submit Interest
        const timeoutFetch = setTimeout(() => {
          gtmCustomEvent(
            {
              eventAction: `Submit Form - Produk ${productName}`,
              eventCategory: "SubmitOrder",
              eventLabel: `Submit Form - Produk ${productName}`,
            },
            "LeadProduct",
          );
        }, 1000);
        return () => window.clearTimeout(timeoutFetch);
      } else {
        dispatch(
          setOpenSnackbar({
            message: message,
            variant: "failed",
          }),
        );
      }
      actions.resetForm();
    } catch (err: any) {
      dispatch(
        setOpenSnackbar({
          message: err?.message,
          variant: "failed",
        }),
      );
      setIsLoad(false);
    } finally {
      setIsLoad(false);
    }
  };

  const onAgreeTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.currentTarget.checked);
  };

  useEffect(() => {
    _getProfileUser();
    _getDataIndustry();
    // _getDataCountry();
    _getDataCompanySize();
  }, [language]);

  useEffect(() => {
    _getDataCity();
  }, [initialCityParam]);

  return {
    initialValueForm,
    dialCode,
    // countryData,
    industryData,
    cityData,
    companySizeData,
    isLoad,
    onCityChange,
    onSubmitForm,
    isChecked,
    setIsChecked,
    onAgreeTermChange,
    setDialCode,
    language,
  };
}

export default useAction;
